import React, { useState } from 'react';
import type { ScheduleAggregateFilters } from '../../../../types/workflows';
import ToastService from '../../../../AppSettings/services/ToastService';
import './ScheduleExportModal.css';

export interface ScheduleExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: ExportOptions) => Promise<void>;
  filters?: ScheduleAggregateFilters;
  projectCount?: number;
}

export interface ExportOptions {
  format: 'png' | 'pdf';
  includeLegend: boolean;
  includeFilters: boolean;
}

/**
 * ScheduleExportModal Component
 * 
 * Modal for exporting schedule/Gantt view as PNG or PDF.
 * 
 * Features:
 * - Format selection (PNG/PDF)
 * - Include legend checkbox
 * - Include filters checkbox
 * - Progress indicator
 * - Tenant-aware filename generation
 */
const ScheduleExportModal: React.FC<ScheduleExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
  filters,
  projectCount = 0,
}) => {
  const [format, setFormat] = useState<'png' | 'pdf'>('pdf');
  const [includeLegend, setIncludeLegend] = useState(true);
  const [includeFilters, setIncludeFilters] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  if (!isOpen) {
    return null;
  }

  const handleExport = async () => {
    let progressInterval: ReturnType<typeof setInterval> | undefined;
    let closeTimeout: ReturnType<typeof setTimeout> | undefined;

    try {
      setIsExporting(true);
      setProgress(0);

      progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            if (progressInterval) clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      await onExport({
        format,
        includeLegend,
        includeFilters,
      });

      if (progressInterval) clearInterval(progressInterval);
      setProgress(100);

      closeTimeout = setTimeout(() => {
        onClose();
        setIsExporting(false);
        setProgress(0);
      }, 500);
    } catch (error) {
      if (progressInterval) clearInterval(progressInterval);
      if (closeTimeout) clearTimeout(closeTimeout);
      console.error('Export error:', error);
      ToastService.error('Failed to export schedule. Please try again.');
      setIsExporting(false);
      setProgress(0);
    }
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  return (
    <div className="schedule-export-modal-overlay" onClick={handleClose}>
      <div
        className="schedule-export-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="export-modal-title"
        aria-modal="true"
      >
        <div className="export-modal-header">
          <h2 id="export-modal-title">Export Schedule</h2>
          <button
            className="export-modal-close"
            onClick={handleClose}
            disabled={isExporting}
            aria-label="Close export modal"
          >
            ×
          </button>
        </div>

        <div className="export-modal-content">
          <div className="export-info">
            <p>
              Exporting {projectCount} project{projectCount !== 1 ? 's' : ''} from the schedule view.
            </p>
          </div>

          {/* Format Selection */}
          <div className="export-option-group">
            <label className="export-option-label">Export Format</label>
            <div className="format-options">
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="png"
                  checked={format === 'png'}
                  onChange={(e) => setFormat(e.target.value as 'png' | 'pdf')}
                  disabled={isExporting}
                />
                <span>PNG Image</span>
              </label>
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={(e) => setFormat(e.target.value as 'png' | 'pdf')}
                  disabled={isExporting}
                />
                <span>PDF Document</span>
              </label>
            </div>
          </div>

          {/* Options */}
          <div className="export-option-group">
            <label className="export-option-label">Include in Export</label>
            <div className="checkbox-options">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={includeLegend}
                  onChange={(e) => setIncludeLegend(e.target.checked)}
                  disabled={isExporting}
                />
                <span>Include Legend</span>
              </label>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={includeFilters}
                  onChange={(e) => setIncludeFilters(e.target.checked)}
                  disabled={isExporting}
                />
                <span>Include Active Filters</span>
              </label>
            </div>
          </div>

          {/* Progress Indicator */}
          {isExporting && (
            <div className="export-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="progress-text">
                {progress < 100 ? 'Exporting...' : 'Export complete!'}
              </p>
            </div>
          )}
        </div>

        <div className="export-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isExporting}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExport}
            disabled={isExporting || projectCount === 0}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleExportModal;

